import { useState } from 'react'
import type { SlotDefinition } from '../../types/communications'

interface SlotValueFormProps {
    schema: SlotDefinition[]
    values: Record<string, unknown>
    onChange: (values: Record<string, unknown>) => void
    onAIFill?: () => void
    onAIRefine?: () => void
    aiBusy?: boolean
}

export function SlotValueForm({
    schema,
    values,
    onChange,
    onAIFill,
    onAIRefine,
    aiBusy,
}: SlotValueFormProps) {
    const set = (name: string, v: unknown) => onChange({ ...values, [name]: v })

    return (
        <div className="space-y-4">
            {(onAIFill || onAIRefine) && (
                <div className="flex gap-2">
                    {onAIFill && (
                        <button
                            type="button"
                            onClick={onAIFill}
                            disabled={aiBusy}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
                        >
                            {aiBusy ? 'Thinking…' : 'Fill with AI'}
                        </button>
                    )}
                    {onAIRefine && (
                        <button
                            type="button"
                            onClick={onAIRefine}
                            disabled={aiBusy}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
                        >
                            Refine…
                        </button>
                    )}
                </div>
            )}
            {schema.map(slot => (
                <SlotField
                    key={slot.name}
                    slot={slot}
                    value={values[slot.name]}
                    onChange={v => set(slot.name, v)}
                />
            ))}
        </div>
    )
}

function SlotField({
    slot,
    value,
    onChange,
}: {
    slot: SlotDefinition
    value: unknown
    onChange: (v: unknown) => void
}) {
    const label = (
        <label className="mb-1 block text-sm font-medium text-gray-700">
            {slot.name}
            {slot.required && <span className="ml-1 text-red-500">*</span>}
            {slot.hint && (
                <span className="ml-2 text-xs font-normal text-gray-500">{slot.hint}</span>
            )}
        </label>
    )

    if (slot.type === 'listText') {
        const items = Array.isArray(value) ? (value as string[]) : []
        return (
            <div>
                {label}
                <ListTextEditor items={items} onChange={onChange} maxLen={slot.maxLen} />
            </div>
        )
    }

    const shared =
        'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
    if (slot.type === 'longText' || slot.type === 'markdown') {
        return (
            <div>
                {label}
                <textarea
                    className={`${shared} min-h-[120px]`}
                    value={typeof value === 'string' ? value : ''}
                    maxLength={slot.maxLen}
                    onChange={e => onChange(e.target.value)}
                />
            </div>
        )
    }
    return (
        <div>
            {label}
            <input
                type={slot.type === 'url' ? 'url' : 'text'}
                className={shared}
                value={typeof value === 'string' ? value : ''}
                maxLength={slot.maxLen}
                onChange={e => onChange(e.target.value)}
            />
        </div>
    )
}

function ListTextEditor({
    items,
    onChange,
    maxLen,
}: {
    items: string[]
    onChange: (v: string[]) => void
    maxLen?: number
}) {
    const [draft, setDraft] = useState('')
    return (
        <div className="space-y-1">
            {items.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                    <input
                        value={item}
                        maxLength={maxLen}
                        onChange={e => {
                            const next = [...items]
                            next[i] = e.target.value
                            onChange(next)
                        }}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <button
                        type="button"
                        onClick={() => onChange(items.filter((_, j) => j !== i))}
                        className="text-sm text-gray-500 hover:text-red-600"
                    >
                        ×
                    </button>
                </div>
            ))}
            <div className="flex gap-2">
                <input
                    value={draft}
                    maxLength={maxLen}
                    onChange={e => setDraft(e.target.value)}
                    placeholder="Add item…"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    onKeyDown={e => {
                        if (e.key === 'Enter' && draft.trim()) {
                            e.preventDefault()
                            onChange([...items, draft.trim()])
                            setDraft('')
                        }
                    }}
                />
            </div>
        </div>
    )
}
